import React, { FC, Fragment } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { compose } from '@reduxjs/toolkit';

import type { BettingContextProps } from '../../types';
import type { ReduxState } from 'app/redux/types';

import { withinBettingContext } from 'app/contexts/betting';
import { ArrowDivider, ArrowSize } from 'app/design-system/components/ArrowDivider';
import { SportId } from 'app/lib/betting/constants';
import { getSportKey } from 'app/lib/betting/sport-utils';
import { BreadcrumbType } from 'app/redux/betting-presentation/grid/constants';
import type { GridSubheaderBreadcrumbs } from 'app/redux/betting-presentation/grid/selectors';

import { getDataGaEventByNamespace } from '../../utils/data-ga-event-by-namespace';
import BreadcrumbLink from '../Breadcrumb/BreadcrumbLink';

import styles from '../Breadcrumb/Breadcrumb.module.scss';

type StoreProps = {
  breadcrumbs: GridSubheaderBreadcrumbs;
};

type ContextProps = {
  $betting: BettingContextProps;
};

type GridStageNameProps = {
  sportId: SportId;
  tournamentStageId: number;
};

const GridStageName: FC<GridStageNameProps & StoreProps & ContextProps> = ({
  breadcrumbs,
  sportId,
  $betting: { namespace },
}) => {
  return (
    <div className={classNames(styles.tournamentName, styles[getSportKey(sportId)])}>
      <div className={styles.stagesBreadcrumbs}>
        {breadcrumbs.map((breadcrumb, index) => {
          if (breadcrumb.type === BreadcrumbType.SPORT) {
            return null;
          }

          const notLastBreadcrumb = !(index === breadcrumbs.length - 1);

          return (
            <Fragment key={breadcrumb.type}>
              <BreadcrumbLink breadcrumb={breadcrumb}
                              dataGaEvent={getDataGaEventByNamespace(namespace, 'crumbsClick')} />

              {notLastBreadcrumb && (
                <ArrowDivider icon="micro-chevron-right"
                              size={ArrowSize.BIG} />
              )}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

const mapStateToProps = (
  state: ReduxState,
  {
    $betting: {
      content: {
        selectors: { tournamentStages },
      },
      grid: {
        selectors: { gridSubheaderBreadcrumbSelector },
      },
    },
    tournamentStageId,
  }: BettingContextProps & GridStageNameProps,
): StoreProps => {
  const stage = tournamentStages(state).get(tournamentStageId);

  return {
    breadcrumbs: gridSubheaderBreadcrumbSelector(state, [...stage?.matchIds][0]),
  };
};

export default compose<FC<GridStageNameProps>>(
  withinBettingContext,
  connect(mapStateToProps),
)(GridStageName);
